import { useState, useEffect, FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Paper,
  FormHelperText,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';
import { format, parseISO } from 'date-fns';

interface Farmer {
  id: string;
  name: string;
}

interface Variety {
  id: string;
  name: string;
  type: 'crop' | 'animal';
}

const ProductionPlanForm: FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [varieties, setVarieties] = useState<Variety[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Form data state with proper typing
  const [formData, setFormData] = useState<{
    plan_name: string;
    production_type: 'crop' | 'animal';
    variety_id: string;
    farmer_id: string;
    planned_start_date: Date | null;
    planned_end_date: Date | null;
    target_area_hectares: string | number;
    target_animal_count: string | number;
    description: string;
  }>({
    plan_name: '',
    production_type: 'crop',
    variety_id: '',
    farmer_id: '',
    planned_start_date: null,
    planned_end_date: null,
    target_area_hectares: '',
    target_animal_count: '',
    description: '',
  });

  // Fetch farmers
  const fetchFarmers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users?role=farmer`);
      setFarmers(response.data.map((user: any) => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
      })));
    } catch (error) {
      console.error('Error fetching farmers:', error);
      toast.error('Failed to load farmers');
    }
  };

  // Fetch varieties based on production type
  const fetchVarieties = async (type: 'crop' | 'animal') => {
    try {
      const endpoint = type === 'crop' 
        ? `${API_BASE_URL}/crop-varieties`
        : `${API_BASE_URL}/animal-breeds`;
      
      const response = await axios.get(endpoint);
      setVarieties(response.data.map((item: any) => ({
        id: item.id,
        name: item.name,
        type,
      })));
    } catch (error) {
      console.error(`Error fetching ${type} varieties:`, error);
      toast.error(`Failed to load ${type} varieties`);
    }
  };

  // Fetch plan data if in edit mode
  const fetchPlan = async (planId: string) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/production-plans/${planId}`);
      const data = response.data;
      
      setFormData({
        plan_name: data.plan_name,
        production_type: data.production_type,
        variety_id: data.variety_id,
        farmer_id: data.farmer_id,
        planned_start_date: data.planned_start_date ? 
          (typeof data.planned_start_date === 'string' ? parseISO(data.planned_start_date) : data.planned_start_date) : 
          null,
        planned_end_date: data.planned_end_date ? 
          (typeof data.planned_end_date === 'string' ? parseISO(data.planned_end_date) : data.planned_end_date) : 
          null,
        target_area_hectares: data.target_area_hectares,
        target_animal_count: data.target_animal_count,
        description: data.description || '',
      });
      
      // Fetch varieties for the plan's production type
      await fetchVarieties(data.production_type);
    } catch (error) {
      console.error('Error fetching production plan:', error);
      toast.error('Failed to load production plan');
      navigate('/production-plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFarmers();
    
    if (isEditMode && id) {
      fetchPlan(id);
    } else {
      // For new plans, fetch crop varieties by default
      fetchVarieties('crop');
    }
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user types
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user selects an option
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleProductionTypeChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      production_type: value as 'crop' | 'animal',
      variety_id: '', // Reset variety when type changes
    }));
    
    // Fetch varieties for the selected type
    fetchVarieties(value as 'crop' | 'animal');
    
    // Clear errors
    setErrors({
      ...errors,
      production_type: '',
      variety_id: '',
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.plan_name.trim()) {
      newErrors.plan_name = 'Plan name is required';
    }
    
    if (!formData.farmer_id) {
      newErrors.farmer_id = 'Please select a farmer';
    }
    
    if (!formData.variety_id) {
      newErrors.variety_id = `Please select a ${formData.production_type} variety`;
    }
    
    if (!formData.planned_start_date) {
      newErrors.planned_start_date = 'Start date is required';
    }
    
    if (formData.production_type === 'crop' && !formData.target_area_hectares) {
      newErrors.target_area_hectares = 'Target area is required for crop plans';
    }
    
    if (formData.production_type === 'animal' && !formData.target_animal_count) {
      newErrors.target_animal_count = 'Animal count is required for animal plans';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      const payload = {
        ...formData,
        planned_start_date: formData.planned_start_date ? format(formData.planned_start_date, 'yyyy-MM-dd') : null,
        planned_end_date: formData.planned_end_date ? format(formData.planned_end_date, 'yyyy-MM-dd') : null,
        target_area_hectares: formData.production_type === 'crop' ? formData.target_area_hectares : null,
        target_animal_count: formData.production_type === 'animal' ? formData.target_animal_count : null
      };
      
      if (isEditMode && id) {
        await axios.put(`${API_BASE_URL}/production-plans/${id}`, payload);
        toast.success('Production plan updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/production-plans`, payload);
        toast.success('Production plan created successfully');
      }
      
      navigate('/production-plans');
    } catch (error) {
      console.error('Error saving production plan:', error);
      toast.error('Failed to save production plan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
        <Typography variant="h5" component="h1" gutterBottom>
          {isEditMode ? 'Edit Production Plan' : 'Create New Production Plan'}
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name"
                name="plan_name"
                value={formData.plan_name}
                onChange={handleChange}
                error={!!errors.plan_name}
                helperText={errors.plan_name}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.production_type}>
                <InputLabel id="production-type-label">Production Type</InputLabel>
                <Select
                  labelId="production-type-label"
                  id="production_type"
                  name="production_type"
                  value={formData.production_type}
                  onChange={handleProductionTypeChange}
                  label="Production Type"
                  required
                >
                  <MenuItem value="crop">Crop</MenuItem>
                  <MenuItem value="animal">Animal</MenuItem>
                </Select>
                {errors.production_type && (
                  <FormHelperText>{errors.production_type}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.variety_id}>
                <InputLabel id="variety-label">
                  {formData.production_type === 'crop' ? 'Crop Variety' : 'Animal Breed'}
                </InputLabel>
                <Select
                  labelId="variety-label"
                  id="variety_id"
                  name="variety_id"
                  value={formData.variety_id}
                  onChange={handleSelectChange}
                  label={formData.production_type === 'crop' ? 'Crop Variety' : 'Animal Breed'}
                  required
                >
                  {varieties.map((variety) => (
                    <MenuItem key={variety.id} value={variety.id}>
                      {variety.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.variety_id && (
                  <FormHelperText>{errors.variety_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.farmer_id}>
                <InputLabel id="farmer-label">Farmer</InputLabel>
                <Select
                  labelId="farmer-label"
                  id="farmer_id"
                  name="farmer_id"
                  value={formData.farmer_id}
                  onChange={handleSelectChange}
                  label="Farmer"
                  required
                >
                  {farmers.map((farmer) => (
                    <MenuItem key={farmer.id} value={farmer.id}>
                      {farmer.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.farmer_id && (
                  <FormHelperText>{errors.farmer_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Planned Start Date"
                value={formData.planned_start_date}
                onChange={(date: Date | null) => {
                  setFormData(prev => ({
                    ...prev,
                    planned_start_date: date,
                  }));
                  
                  if (errors.planned_start_date) {
                    setErrors(prev => ({
                      ...prev,
                      planned_start_date: 'Please select a valid start date'
                    }));
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.planned_start_date,
                    helperText: typeof errors.planned_start_date === 'string' ? errors.planned_start_date : '',
                    required: true,
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Planned End Date (Optional)"
                value={formData.planned_end_date}
                onChange={(date: Date | null) => {
                  setFormData(prev => ({
                    ...prev,
                    planned_end_date: date,
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            
            {formData.production_type === 'crop' ? (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Target Area (Hectares)"
                  name="target_area_hectares"
                  value={formData.target_area_hectares || ''}
                  onChange={handleChange}
                  error={!!errors.target_area_hectares}
                  helperText={errors.target_area_hectares}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  required
                />
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Target Animal Count"
                  name="target_animal_count"
                  value={formData.target_animal_count || ''}
                  onChange={handleChange}
                  error={!!errors.target_animal_count}
                  helperText={errors.target_animal_count}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add any additional notes or details about this production plan"
              />
            </Grid>
            
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/production-plans')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
              >
                {isEditMode ? 'Update Plan' : 'Create Plan'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </LocalizationProvider>
  );
};

export default ProductionPlanForm;
